import { getAllJobs } from "./jobStorage";
import {
  queueCloudRecordDelete,
  queueCloudRecordSave,
} from "./workspaceCloudSync";
import { APP_ROUTE_PATHS } from "../appRoutePaths";

const STORE_PREFIX = "manucalc_shop_records_v1";

export const TOOL_LIBRARY_CATEGORIES = [
  "Cutters",
  "Endmills",
  "Drills",
  "Taps",
  "Reamers",
  "Thread mills",
  "Lathe tooling",
  "Indexable inserts",
  "Toolholders / arbors",
  "Boring bars",
  "Chamfer / deburr tools",
  "Countersinks / counterbores",
  "Abrasives",
  "Measuring tools / gauges",
  "Workholding consumables",
  "Lubricants / coolants",
  "Saw blades",
  "Additive consumables",
  "Welding consumables",
  "Specialty tooling",
];

export const TOOL_LIBRARY_TYPES = [
  "Endmill",
  "Drill",
  "Tap",
  "Thread mill",
  "Reamer",
  "Insert",
  "Indexable holder",
  "Lathe turning tool",
  "Lathe grooving tool",
  "Lathe threading tool",
  "Boring bar",
  "Face mill",
  "Shell mill",
  "Slitting saw",
  "Chamfer mill",
  "Countersink",
  "Counterbore",
  "Deburring tool",
  "Broach",
  "Form tool",
  "Hob / gear cutter",
  "Coolant",
  "Cutting oil",
  "Tapping fluid",
  "Abrasive wheel",
  "Gauge / inspection tool",
  "Fixture consumable",
  "Specialty tool",
];

export const MACHINE_LIBRARY_TYPES = [
  "CNC mill",
  "CNC lathe",
  "Mill-turn",
  "Swiss lathe",
  "Manual mill",
  "Manual lathe",
  "Drill press",
  "Horizontal machining center",
  "Vertical machining center",
  "Wire EDM",
  "Sinker EDM",
  "Surface grinder",
  "Cylindrical grinder",
  "Saw",
  "Press brake",
  "CNC plasma",
  "Laser cutter",
  "Waterjet",
  "Welder",
  "Additive / 3D printer",
  "CMM / inspection",
  "Other",
];

export const MACHINE_CONTROL_TYPES = [
  "Haas NGC",
  "Classic Haas",
  "Fanuc",
  "Siemens",
  "Mazak / Mazatrol",
  "Okuma OSP",
  "Mitsubishi",
  "Heidenhain",
  "Hurco",
  "Fadal",
  "Centroid",
  "LinuxCNC",
  "Manual / DRO",
  "Other",
];

export const RECORD_APPROVAL_STATUSES = ["Draft", "Pending review", "Approved", "Rejected"];

export const SHOP_RECORD_TYPES = {
  tools: {
    title: "Tool Library",
    singular: "Tool",
    storageKey: `${STORE_PREFIX}_tools`,
    route: APP_ROUTE_PATHS.tools,
    description: "Track cutters, drills, taps, inserts, holders, gauges, coolants, consumables, tool life, wear, regrinds, feeds/speeds, and storage notes.",
    fields: [
      ["name", "Tool name", "text", true],
      ["toolCategory", "Tool category", "select", true, TOOL_LIBRARY_CATEGORIES],
      ["toolType", "Tool type", "select", true, TOOL_LIBRARY_TYPES],
      ["processFamily", "Process / operation family", "select", false, ["Milling", "Drilling", "Tapping", "Threading", "Turning", "Boring", "Grooving", "Parting", "Reaming", "Chamfering", "Deburring", "Sawing", "Grinding", "Inspection", "Coolant / lubrication", "Welding", "Additive", "General shop"]],
      ["diameter", "Diameter", "number"],
      ["cornerRadius", "Corner radius", "number"],
      ["fluteCount", "Flute count", "number"],
      ["length", "Overall length", "number"],
      ["cutLength", "Length of cut", "number"],
      ["stickout", "Stickout", "number"],
      ["shankSize", "Shank / holder size", "text"],
      ["threadDesignation", "Thread designation", "text"],
      ["hand", "Hand", "select", false, ["Right hand", "Left hand", "Neutral", "N/A"]],
      ["toolMaterial", "Tool material", "select", false, ["HSS", "Cobalt", "Powder metal", "Solid carbide", "Indexable carbide", "Brazed carbide", "Ceramic", "Cermet", "CBN", "PCD", "Diamond coated", "Abrasive", "Coolant / lubricant", "Gauge steel", "Other"]],
      ["coating", "Coating / grade", "text"],
      ["insertStyle", "Insert style", "text"],
      ["insertGrade", "Insert grade", "text"],
      ["noseRadius", "Nose radius", "number"],
      ["edgeCount", "Edge count", "number"],
      ["manufacturer", "Manufacturer", "text"],
      ["partNumber", "Part number", "text"],
      ["serialOrLot", "Serial / lot number", "text"],
      ["preferredMaterials", "Preferred materials", "text"],
      ["machineCompatibility", "Machine compatibility", "text"],
      ["holderCompatibility", "Holder / collet compatibility", "text"],
      ["defaultFeedsSpeeds", "Approved feeds/speeds", "textarea"],
      ["calculatorNotes", "Calculator notes / known good cutting data", "textarea"],
      ["toolCost", "Tool cost", "number"],
      ["replacementCost", "Replacement cost", "number"],
      ["toolLifeEstimate", "Expected tool life", "text"],
      ["usageCount", "Usage count", "number"],
      ["cutMinutes", "Cutting minutes", "number"],
      ["wearPercent", "Wear percent", "number"],
      ["lastUsedOn", "Last used date", "date"],
      ["lastUsedPlan", "Last used job / plan", "text"],
      ["regrindCount", "Regrind / sharpen count", "number"],
      ["nextInspectionDue", "Next inspection due", "date"],
      ["inspectionCriteria", "Tool inspection criteria", "textarea"],
      ["coolantRequirement", "Coolant / lubricant requirement", "text"],
      ["location", "Location/storage", "text"],
      ["status", "Status", "select", false, ["Active", "Needs inspection", "Needs regrind", "Quarantine", "Retired", "Inactive"]],
      ["notes", "Notes", "textarea"],
    ],
  },
  fixtures: {
    title: "Fixture Library",
    singular: "Fixture",
    storageKey: `${STORE_PREFIX}_fixtures`,
    route: APP_ROUTE_PATHS.fixtures,
    description: "Store vises, jaws, chucks, collets, clamps, welding fixtures, and inspection fixtures.",
    fields: [
      ["name", "Fixture name", "text", true],
      ["fixtureType", "Fixture type", "select", true, ["Vise", "Soft jaws", "Chuck", "Collet", "Angle plate", "Clamp", "Vacuum fixture", "Welding fixture", "Inspection fixture", "Custom fixture"]],
      ["machineCompatibility", "Machine compatibility", "text"],
      ["setupNotes", "Setup notes", "textarea"],
      ["workholdingNotes", "Workholding notes", "textarea"],
      ["relatedJobs", "Related jobs/plans", "text"],
      ["location", "Location", "text"],
      ["safetyNotes", "Safety notes", "textarea"],
      ["photoNotes", "Photo/storage notes", "textarea"],
      ["status", "Status", "select", false, ["Active", "Inactive", "Needs repair"]],
    ],
  },
  machines: {
    title: "Machine Database",
    singular: "Machine",
    storageKey: `${STORE_PREFIX}_machines`,
    route: APP_ROUTE_PATHS.machines,
    description: "Track machine capabilities so plans, feeds/speeds, setup sheets, and capacity checks use shop-specific limits.",
    fields: [
      ["name", "Machine name", "text", true],
      ["machineType", "Machine type", "select", true, MACHINE_LIBRARY_TYPES],
      ["manufacturer", "Manufacturer", "text"],
      ["model", "Model", "text"],
      ["year", "Year", "number"],
      ["serialNumber", "Serial number", "text"],
      ["controller", "Control / controller", "select", false, MACHINE_CONTROL_TYPES],
      ["postProcessor", "Post / output style", "text"],
      ["gcodeProfile", "G-code profile / machine preset", "text"],
      ["workEnvelope", "Work envelope / travel", "text"],
      ["xTravel", "X travel", "number"],
      ["yTravel", "Y travel", "number"],
      ["zTravel", "Z travel", "number"],
      ["tableSize", "Table / bed size", "text"],
      ["maxWorkpieceWeight", "Max workpiece weight", "number"],
      ["chuckSize", "Chuck / fixture size", "text"],
      ["barCapacity", "Bar capacity", "number"],
      ["throughSpindleBore", "Through-spindle bore", "number"],
      ["spindleRpm", "Max spindle RPM", "number"],
      ["minSpindleRpm", "Min spindle RPM", "number"],
      ["horsepower", "Horsepower", "number"],
      ["additiveProcess", "Additive process", "select", false, ["FFF / FDM", "SLA / MSLA", "SLS", "MJF", "DMLS / SLM", "Binder jet", "Directed energy deposition", "Other"], { machineTypes: ["Additive / 3D printer"] }],
      ["buildVolume", "Build volume (X x Y x Z)", "text", false, [], { machineTypes: ["Additive / 3D printer"] }],
      ["materialSystem", "Supported material system", "textarea", false, [], { machineTypes: ["Additive / 3D printer"] }],
      ["nozzleDiameter", "Installed nozzle diameter", "number", false, [], { machineTypes: ["Additive / 3D printer"] }],
      ["filamentDiameter", "Filament diameter", "number", false, [], { machineTypes: ["Additive / 3D printer"] }],
      ["averagePowerWatts", "Measured average print power (W)", "number", false, [], { machineTypes: ["Additive / 3D printer"] }],
      ["maxExtruderTemp", "Max extruder temperature", "number", false, [], { machineTypes: ["Additive / 3D printer"] }],
      ["maxBedTemp", "Max bed temperature", "number", false, [], { machineTypes: ["Additive / 3D printer"] }],
      ["heatedChamber", "Heated chamber capability", "text", false, [], { machineTypes: ["Additive / 3D printer"] }],
      ["slicerProfile", "Approved slicer / profile", "text", false, [], { machineTypes: ["Additive / 3D printer"] }],
      ["additivePostProcessing", "Post-processing capability", "textarea", false, [], { machineTypes: ["Additive / 3D printer"] }],
      ["maxFeedrate", "Max feedrate", "number"],
      ["rapidRate", "Rapid rate", "number"],
      ["toolCapacity", "Tool capacity", "number"],
      ["axisCount", "Axis count", "number"],
      ["liveTooling", "Live tooling / rotary capability", "text"],
      ["probeCapability", "Probe / inspection capability", "text"],
      ["coolantCapability", "Coolant capability", "text"],
      ["workholding", "Primary workholding", "textarea"],
      ["supportedOperations", "Supported operations", "textarea"],
      ["excludedOperations", "Operations to avoid", "textarea"],
      ["setupLimitations", "Setup limitations", "textarea"],
      ["accuracyNotes", "Accuracy / repeatability notes", "textarea"],
      ["maintenanceNotes", "Maintenance notes", "textarea"],
      ["availableHoursWeek", "Available hours/week", "number"],
      ["hourlyRate", "Internal hourly rate", "number"],
      ["notes", "Notes", "textarea"],
      ["status", "Status", "select", false, ["Active", "Down", "Maintenance", "Inactive"]],
    ],
  },
  setupSheets: {
    title: "Setup Sheets",
    singular: "Setup Sheet",
    storageKey: `${STORE_PREFIX}_setupSheets`,
    route: APP_ROUTE_PATHS.setupSheets,
    description: "Create mobile-friendly setup sheets from manufacturing plans, tools, fixtures, and inspection checkpoints.",
    fields: [
      ["planId", "Manufacturing plan", "job"],
      ["partName", "Part name", "text", true],
      ["jobName", "Job/plan name", "text"],
      ["material", "Material", "text"],
      ["machine", "Machine", "text"],
      ["operationNumber", "Operation number", "text"],
      ["tools", "Tools", "textarea"],
      ["fixtures", "Fixtures/workholding", "textarea"],
      ["workOffset", "Work offset", "text"],
      ["programName", "Program name", "text"],
      ["setupNotes", "Setup notes", "textarea"],
      ["safetyNotes", "Safety notes", "textarea"],
      ["speedsFeeds", "Speeds/feeds", "textarea"],
      ["inspectionCheckpoints", "Inspection checkpoints", "textarea"],
      ["revision", "Revision", "text"],
      ["operatorSignoff", "Operator signoff", "text"],
      ["photoNotes", "Setup photo notes", "textarea"],
      ["approvalStatus", "Approval status", "select", false, RECORD_APPROVAL_STATUSES],
      ["reviewNotes", "Review notes", "textarea"],
    ],
  },
  jobTravelers: {
    title: "Job Travelers",
    singular: "Traveler",
    storageKey: `${STORE_PREFIX}_jobTravelers`,
    route: APP_ROUTE_PATHS.jobTravelers,
    description: "Release and track jobs through operation sequence, signoffs, inspection, and status.",
    fields: [
      ["planId", "Manufacturing plan", "job"],
      ["jobName", "Job name", "text", true],
      ["partNumber", "Part number", "text"],
      ["revision", "Revision", "text"],
      ["quantity", "Quantity", "number"],
      ["customerReference", "Customer/internal reference", "text"],
      ["material", "Material", "text"],
      ["operationSequence", "Operation sequence", "textarea"],
      ["machineRoute", "Machine route", "textarea"],
      ["setupSheetLinks", "Setup sheet links", "textarea"],
      ["inspectionCheckpoints", "Inspection checkpoints", "textarea"],
      ["signoffSections", "Signoff sections", "textarea"],
      ["dueDate", "Due date", "date"],
      ["status", "Status", "select", false, ["Planned", "Released", "In Process", "Waiting", "Inspection", "Complete", "Hold"]],
      ["notes", "Notes", "textarea"],
      ["approvalStatus", "Approval status", "select", false, RECORD_APPROVAL_STATUSES],
      ["reviewNotes", "Review notes", "textarea"],
    ],
  },
  inspectionSheets: {
    title: "Inspection Sheets",
    singular: "Inspection Sheet",
    storageKey: `${STORE_PREFIX}_inspectionSheets`,
    route: APP_ROUTE_PATHS.inspection,
    description: "Capture dimensional checks, method, gauge, actual measurements, pass/fail, and inspection records.",
    fields: [
      ["planId", "Manufacturing plan", "job"],
      ["partName", "Part name", "text", true],
      ["drawingRevision", "Drawing/revision", "text"],
      ["feature", "Feature/dimension", "text", true],
      ["nominal", "Nominal value", "number"],
      ["upperTolerance", "Upper tolerance", "number"],
      ["lowerTolerance", "Lower tolerance", "number"],
      ["measurementMethod", "Measurement method", "text"],
      ["gaugeUsed", "Gauge/tool used", "text"],
      ["actualMeasurement", "Actual measurement", "number"],
      ["passFail", "Pass/fail", "select", false, ["Not checked", "Pass", "Fail", "Review"]],
      ["inspector", "Inspector", "text"],
      ["inspectionDate", "Date", "date"],
      ["notes", "Notes", "textarea"],
      ["approvalStatus", "Approval status", "select", false, RECORD_APPROVAL_STATUSES],
      ["reviewNotes", "Review notes", "textarea"],
    ],
  },
  firstArticleInspections: {
    title: "First Article Inspections",
    singular: "First Article Inspection",
    storageKey: `${STORE_PREFIX}_firstArticleInspections`,
    route: APP_ROUTE_PATHS.firstArticleInspections,
    description: "Plan and record first-piece/first-article checks against the drawing, revision, ballooned characteristics, measurement method, and final disposition.",
    fields: [
      ["planId", "Manufacturing plan", "job"],
      ["partName", "Part name", "text", true],
      ["partNumber", "Part number", "text"],
      ["drawingRevision", "Drawing / revision", "text", true],
      ["operationNumber", "Operation number", "text"],
      ["characteristicNumber", "Balloon / characteristic number", "text", true],
      ["characteristicDescription", "Characteristic / requirement", "textarea", true],
      ["specification", "Drawing requirement / specification", "text"],
      ["nominal", "Nominal value", "number"],
      ["upperTolerance", "Upper tolerance", "number"],
      ["lowerTolerance", "Lower tolerance", "number"],
      ["measurementMethod", "Measurement method", "select", false, ["Caliper", "Micrometer", "Depth micrometer", "Height gauge", "Bore gauge", "Pin gauge", "Thread gauge", "CMM", "Optical comparator", "Surface plate / indicator", "Visual", "Functional gauge", "Other"]],
      ["gaugeUsed", "Gauge / equipment ID", "text"],
      ["actualMeasurement", "Actual measurement", "number"],
      ["sampleSize", "Sample size", "number"],
      ["inspector", "Inspector", "text"],
      ["inspectionDate", "Inspection date", "date"],
      ["disposition", "Disposition", "select", false, ["Not checked", "Accept", "Reject", "Rework", "Use as is request", "Customer review"]],
      ["correctiveAction", "Corrective action / containment", "textarea"],
      ["notes", "Notes", "textarea"],
      ["approvalStatus", "Approval status", "select", false, RECORD_APPROVAL_STATUSES],
      ["reviewNotes", "Review notes", "textarea"],
    ],
  },
  controlPlans: {
    title: "Control Plans",
    singular: "Control Plan",
    storageKey: `${STORE_PREFIX}_controlPlans`,
    route: APP_ROUTE_PATHS.controlPlans,
    description: "Document process controls for critical product and process characteristics, including method, frequency, owner, and reaction plan.",
    fields: [
      ["planId", "Manufacturing plan", "job"],
      ["planTitle", "Control plan title", "text", true],
      ["partName", "Part name", "text"],
      ["partNumber", "Part number", "text"],
      ["drawingRevision", "Drawing / revision", "text"],
      ["operationNumber", "Operation number", "text"],
      ["processStep", "Process step / operation", "text", true],
      ["machineOrWorkcenter", "Machine / workcenter", "text"],
      ["productCharacteristic", "Product characteristic", "textarea"],
      ["processCharacteristic", "Process characteristic", "textarea"],
      ["specification", "Specification / tolerance", "text"],
      ["controlMethod", "Control method", "select", false, ["First-piece inspection", "In-process inspection", "SPC chart", "Go/no-go gauge", "CMM inspection", "Visual inspection", "Functional test", "Setup verification", "Preventive maintenance", "Operator checklist", "Other"]],
      ["sampleSize", "Sample size", "text"],
      ["sampleFrequency", "Sample frequency", "text"],
      ["gaugeOrTool", "Gauge / tool / system", "text"],
      ["responsibleRole", "Responsible role", "select", false, ["Operator", "Setup technician", "Inspector", "Quality", "Supervisor", "Engineer", "Maintenance", "Other"]],
      ["reactionPlan", "Reaction plan if out of control / out of spec", "textarea", true],
      ["recordRetention", "Record retention / evidence", "text"],
      ["status", "Status", "select", false, ["Draft", "Active", "Under review", "On hold", "Retired"]],
      ["notes", "Notes", "textarea"],
      ["approvalStatus", "Approval status", "select", false, RECORD_APPROVAL_STATUSES],
      ["reviewNotes", "Review notes", "textarea"],
    ],
  },
  shopBrainNotes: {
    title: "Shop Brain",
    singular: "Knowledge Note",
    storageKey: `${STORE_PREFIX}_shopBrainNotes`,
    route: APP_ROUTE_PATHS.shopBrain,
    description: "Capture lessons learned, setup notes, material notes, machine notes, tooling notes, and tribal knowledge.",
    fields: [
      ["title", "Title", "text", true],
      ["noteType", "Note type", "select", true, ["Lesson learned", "Shop tip", "Troubleshooting", "Setup note", "Material note", "Machine note", "Tooling note", "Customer/job note"]],
      ["tags", "Tags", "text"],
      ["linkedRecord", "Linked plan/tool/machine/SOP", "text"],
      ["body", "Knowledge note", "textarea", true],
      ["visibility", "Visibility", "select", false, ["Personal", "Company"]],
      ["approvalStatus", "Approval status", "select", false, RECORD_APPROVAL_STATUSES],
      ["reviewNotes", "Review notes", "textarea"],
    ],
  },
  workspaceTasks: {
    title: "Workspace Tasks",
    singular: "Workspace Task",
    storageKey: `${STORE_PREFIX}_workspaceTasks`,
    route: APP_ROUTE_PATHS.workspace,
    description: "Track manufacturing workspace tasks tied to plans, drawings, messages, approvals, tools, machines, and shop actions.",
    fields: [
      ["title", "Task title", "text", true],
      ["status", "Status", "select", false, ["Open", "In Progress", "Blocked", "Completed"]],
      ["priority", "Priority", "select", false, ["Low", "Medium", "High"]],
      ["assignee", "Assignee", "text"],
      ["sourceType", "Source type", "text"],
      ["sourceId", "Source ID", "text"],
      ["sourceLabel", "Source label", "text"],
      ["dueDate", "Due date", "date"],
      ["notes", "Task notes", "textarea"],
    ],
  },
  drawingReviews: {
    title: "Drawing Reviews",
    singular: "Drawing Review",
    storageKey: `${STORE_PREFIX}_drawingReviews`,
    route: APP_ROUTE_PATHS.workspace,
    description: "Store human-reviewable AI drawing workflow results without claiming unsupported CAD or print interpretation.",
    fields: [
      ["planId", "Manufacturing plan", "job"],
      ["fileName", "File name", "text", true],
      ["fileType", "File type", "text"],
      ["sourcePath", "Source URL / storage path", "text"],
      ["sourcePlan", "Source plan", "text"],
      ["sourcePart", "Source part", "text"],
      ["drawingRevision", "Drawing revision", "text"],
      ["reviewConfidence", "Review confidence", "select", false, ["Metadata only", "User-entered facts", "AI-assisted draft", "Human verified", "Unsupported"]],
      ["reviewStatus", "Review status", "select", false, ["Draft", "Needs Human Review", "Reviewed", "Blocked"]],
      ["detectedFeatures", "Observed / user-supplied features", "textarea"],
      ["suggestedOperations", "Suggested operations", "textarea"],
      ["suggestedMachines", "Suggested machines", "textarea"],
      ["suggestedTooling", "Suggested tooling", "textarea"],
      ["inspectionPoints", "Suggested inspection points", "textarea"],
      ["manufacturingQuestions", "Manufacturing questions", "textarea"],
      ["missingInformation", "Missing information", "textarea"],
      ["limitations", "Review limitations", "textarea"],
      ["recommendedRecords", "Recommended ManuCalc records", "textarea"],
      ["approvalStatus", "Approval status", "select", false, RECORD_APPROVAL_STATUSES],
      ["reviewNotes", "Review notes", "textarea"],
    ],
  },
  quoteAnalyses: {
    title: "Quote Analyses",
    singular: "Quote Analysis",
    storageKey: `${STORE_PREFIX}_quoteAnalyses`,
    route: APP_ROUTE_PATHS.quoteAnalyses,
    description: "Store manufacturing quote intelligence, cost drivers, assumptions, and review notes.",
    fields: [
      ["planId", "Manufacturing plan", "job"],
      ["quoteTitle", "Quote title", "text", true],
      ["materialCost", "Material cost", "number"],
      ["machineCost", "Machine cost", "number"],
      ["laborCost", "Labor cost", "number"],
      ["toolingCost", "Tooling cost", "number"],
      ["overheadCost", "Overhead cost", "number"],
      ["totalCost", "Total cost", "number"],
      ["recommendedSalePrice", "Recommended sale price", "number"],
      ["grossMargin", "Gross margin %", "number"],
      ["costDrivers", "Cost drivers", "textarea"],
      ["costReductionIdeas", "Cost reduction opportunities", "textarea"],
      ["assumptions", "Assumptions to verify", "textarea"],
      ["approvalStatus", "Approval status", "select", false, RECORD_APPROVAL_STATUSES],
      ["reviewNotes", "Review notes", "textarea"],
    ],
  },
};

const safeParse = (value, fallback = []) => {
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
};

export const createRecordId = (prefix = "record") =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const getRecordConfig = (type) => SHOP_RECORD_TYPES[type];

const blankToNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const roundCurrency = (value) => Math.round(value * 100) / 100;
const roundPercent = (value) => Math.round(value * 100) / 100;

export const deriveQuoteAnalysisFields = (record = {}) => {
  const costFields = ["materialCost", "machineCost", "laborCost", "toolingCost", "overheadCost"];
  const costs = costFields.map((field) => blankToNumber(record[field])).filter((value) => value !== null);
  const hasCostInput = costs.length > 0;
  const existingTotal = blankToNumber(record.totalCost);
  const totalCost = hasCostInput
    ? roundCurrency(costs.reduce((sum, value) => sum + value, 0))
    : existingTotal;
  const salePrice = blankToNumber(record.recommendedSalePrice);
  const grossMargin =
    salePrice && salePrice > 0 && totalCost !== null
      ? roundPercent(((salePrice - totalCost) / salePrice) * 100)
      : blankToNumber(record.grossMargin);

  return {
    ...record,
    totalCost: totalCost === null ? record.totalCost || "" : totalCost,
    grossMargin: grossMargin === null ? record.grossMargin || "" : grossMargin,
  };
};

export const prepareRecordApproval = ({ record = {}, previous = {}, user = null, canApprove = false } = {}) => {
  const approvalStatus = record.approvalStatus || previous.approvalStatus || "Draft";
  if (!RECORD_APPROVAL_STATUSES.includes(approvalStatus)) {
    return { errors: ["Select a valid approval status."] };
  }
  if (["Approved", "Rejected"].includes(approvalStatus) && !canApprove) {
    return { errors: ["Only an owner or supervisor can approve or reject this record."] };
  }

  const changed = approvalStatus !== (previous.approvalStatus || "Draft");
  const next = { ...record, approvalStatus };
  if (!changed) return { record: next, changed: false };

  const now = new Date().toISOString();
  const reviewerId = user?.uid || "local-user";
  const reviewerName = user?.displayName || user?.email || reviewerId;
  if (["Approved", "Rejected"].includes(approvalStatus)) {
    next.reviewedBy = reviewerId;
    next.reviewedByName = reviewerName;
    next.reviewedAt = now;
  } else {
    next.reviewedBy = "";
    next.reviewedByName = "";
    next.reviewedAt = "";
  }
  if (approvalStatus === "Approved") {
    next.approvedBy = reviewerId;
    next.approvedByName = reviewerName;
    next.approvedAt = now;
  } else {
    next.approvedBy = "";
    next.approvedByName = "";
    next.approvedAt = "";
  }
  return { record: next, changed: true, previousStatus: previous.approvalStatus || "Draft" };
};

export const getShopRecordHealthEvent = (type, current = {}, previous = {}) => {
  if (type === "tools") {
    const alertStatuses = ["Needs inspection", "Needs regrind", "Quarantine"];
    const currentWear = Number(current.wearPercent);
    const previousWear = Number(previous.wearPercent);
    const crossedWearThreshold = Number.isFinite(currentWear) && currentWear >= 80 && (!Number.isFinite(previousWear) || previousWear < 80);
    const enteredAlertStatus = alertStatuses.includes(current.status) && current.status !== previous.status;
    if (!crossedWearThreshold && !enteredAlertStatus) return null;
    const reason = enteredAlertStatus
      ? `status changed to ${current.status}`
      : `wear reached ${currentWear.toFixed(0)}%`;
    return {
      type: "toolAttention",
      title: "Tool requires attention",
      preview: `${current.name || "Tool"}: ${reason}.`,
      contextType: "tool",
      metadata: { status: current.status || "", wearPercent: Number.isFinite(currentWear) ? currentWear : "" },
    };
  }

  if (type === "machines") {
    const alertStatuses = ["Down", "Maintenance"];
    if (!alertStatuses.includes(current.status) || current.status === previous.status) return null;
    return {
      type: "machineAttention",
      title: "Machine status changed",
      preview: `${current.name || "Machine"} is now ${current.status}.`,
      contextType: "machine",
      metadata: { status: current.status || "", machineType: current.machineType || "" },
    };
  }

  return null;
};

export const getRecords = (type) => {
  const config = getRecordConfig(type);
  if (!config || typeof window === "undefined") return [];
  return safeParse(window.localStorage.getItem(config.storageKey), [])
    .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
};

export const saveRecords = (type, records) => {
  const config = getRecordConfig(type);
  if (!config || typeof window === "undefined") return [];
  window.localStorage.setItem(config.storageKey, JSON.stringify(records));
  window.dispatchEvent(new CustomEvent("manucalc:shop-records-updated", { detail: { type } }));
  return records;
};

export const getShopRecordTypes = () => Object.keys(SHOP_RECORD_TYPES);

export const mergeCloudRecords = (type, records) => saveRecords(type, records);

export const saveRecord = (type, record, user = null, accountProfile = null) => {
  const records = getRecords(type);
  const existing = records.find((item) => item.id === record.id);
  const now = new Date().toISOString();
  const workspaceCompanyId = accountProfile?.companyId || accountProfile?.companyOwnerUid || "";
  const normalizedRecord = type === "quoteAnalyses" ? deriveQuoteAnalysisFields(record) : record;
  const nextRecord = {
    ...normalizedRecord,
    id: normalizedRecord.id || createRecordId(type),
    ownerId: normalizedRecord.ownerId || user?.uid || "local-user",
    companyId: normalizedRecord.companyId || workspaceCompanyId,
    workspaceScope: workspaceCompanyId ? "company" : "user",
    createdBy: normalizedRecord.createdBy || user?.uid || "local-user",
    updatedBy: user?.uid || "local-user",
    createdAt: existing?.createdAt || normalizedRecord.createdAt || now,
    updatedAt: now,
  };

  const next = existing
    ? records.map((item) => (item.id === nextRecord.id ? nextRecord : item))
    : [nextRecord, ...records];
  saveRecords(type, next);
  queueCloudRecordSave(type, nextRecord);
  return nextRecord;
};

export const deleteRecord = (type, id) => {
  saveRecords(type, getRecords(type).filter((item) => item.id !== id));
  queueCloudRecordDelete(type, id);
};

export const getShopStats = () => {
  const jobs = getAllJobs();
  const travelers = getRecords("jobTravelers");
  const setupSheets = getRecords("setupSheets");
  const inspections = getRecords("inspectionSheets");
  const machines = getRecords("machines");
  const tools = getRecords("tools");
  const fixtures = getRecords("fixtures");
  const notes = getRecords("shopBrainNotes");
  const estimatedHours = jobs.reduce(
    (sum, job) =>
      sum + (job.operations || []).reduce((opSum, op) => opSum + Number(op.setupHours || 0) + Number(op.runHours || 0), 0),
    0
  );

  return {
    activePlans: jobs.length,
    openTravelers: travelers.filter((item) => !["Complete", "Hold"].includes(item.status)).length,
    setupSheets: setupSheets.length,
    inspectionSheets: inspections.length,
    machinesLoaded: machines.filter((machine) => machine.status !== "Inactive").length,
    tools: tools.length,
    fixtures: fixtures.length,
    shopBrainNotes: notes.length,
    estimatedShopHours: Math.round(estimatedHours * 10) / 10,
  };
};

export const buildRecordFromPlan = (type, job) => {
  if (!job) return {};
  const firstPart = job.parts?.[0] || {};
  const operationsText = (job.operations || [])
    .map((operation, index) => `${index + 1}. ${operation.name || operation.operationTypeId || "Operation"} - ${operation.workPerformed || ""}`)
    .join("\n");
  const machineRoute = (job.machineRoute || []).join(", ");

  if (type === "setupSheets") {
    const firstOperation = job.operations?.[0] || {};
    return {
      planId: job.id,
      partName: firstPart.name || job.name || "",
      jobName: job.name || "",
      material: job.materialId || firstPart.materialId || "",
      machine: firstOperation.machineId || machineRoute,
      operationNumber: firstOperation.setupNumber || "1",
      tools: firstOperation.toolsUsed || firstOperation.toolDescription || "",
      fixtures: firstOperation.workholding || "",
      setupNotes: firstOperation.notes || job.notes || "",
      inspectionCheckpoints: firstOperation.inspectionCriteria || "",
      speedsFeeds: `Tool diameter: ${firstOperation.toolDiameter || ""}; Stock diameter: ${firstOperation.stockDiameter || ""}`,
      revision: job.revision || "A",
    };
  }

  if (type === "jobTravelers") {
    return {
      planId: job.id,
      jobName: job.name || "",
      partNumber: job.partNumber || firstPart.partNumber || "",
      revision: job.revision || "",
      quantity: job.quantity || firstPart.quantity || 1,
      customerReference: job.customer || "",
      material: job.materialId || firstPart.materialId || "",
      operationSequence: operationsText,
      machineRoute,
      inspectionCheckpoints: (job.operations || []).map((op) => op.inspectionCriteria).filter(Boolean).join("\n"),
      dueDate: job.dueDate || "",
      status: "Planned",
      notes: job.notes || "",
    };
  }

  if (type === "inspectionSheets") {
    return {
      planId: job.id,
      partName: firstPart.name || job.name || "",
      drawingRevision: job.revision || "",
      feature: "Critical feature from plan",
      measurementMethod: "Enter inspection method",
      passFail: "Not checked",
      inspectionDate: new Date().toISOString().slice(0, 10),
      notes: job.notes || "",
    };
  }

  if (type === "quoteAnalyses") {
    const materialCost = Number(job.costing?.materialCost || 0);
    const toolingCost = Number(job.costing?.toolingCost || 0);
    const outsideProcessingCost = Number(job.costing?.outsideProcessingCost || 0);
    const overheadCost = Number(job.costing?.overheadCost || 0);
    return deriveQuoteAnalysisFields({
      planId: job.id,
      quoteTitle: `${job.name || "Manufacturing Plan"} Quote Review`,
      materialCost,
      machineCost: "",
      laborCost: "",
      toolingCost: toolingCost + outsideProcessingCost,
      overheadCost,
      totalCost: "",
      recommendedSalePrice: "",
      grossMargin: "",
      costDrivers: [
        operationsText,
        outsideProcessingCost ? `Outside processing included in tooling/consumables bucket: $${outsideProcessingCost.toFixed(2)}.` : "",
      ].filter(Boolean).join("\n") || "Enter operation, material, tooling, labor, machine, outside processing, and overhead cost drivers.",
      costReductionIdeas: "",
      assumptions: "Verify material cost, quantity, cycle time, setup time, labor rate, machine rate, scrap allowance, outside processing, overhead, and target margin before quoting.",
      approvalStatus: "Draft",
      reviewNotes: job.notes || "",
    });
  }

  return {};
};
